import React, {ReactNode, useEffect, useState} from 'react'
import ReactMarkdown from 'react-markdown'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {PDPAwardSlot, ProductInformation} from '../../../types/cms'
import {Box, BoxProps, Flex, Stack, Text, useDisclosure} from '../../../vanilla'
import {CollapsablePanel} from '../../collapsable-panel'
import {ChevronUpIcon} from '../../icons'
import {TabPanel, Tabs} from '../../tabs'
import FeefoReviews from '../../feefo-reviews'
import {NutritionalModal} from './nutritional-modal'
import {useProductDetail} from '../../../store/hooks/useProductDetail'
import {CookingInstructions} from './cooking-instructions'
import {infoLeftColumn, pdpAccordionToggle} from './styles.css'
import {useCustomerStore, useGlobalStore, useContentStore} from '../../../store/hooks/useStore'
import {Slot} from '../../cms/Slot'
import {SlotContentItem} from '../../../types/cms'
import { capitalize, toSlug } from '../../../utils/utils'
import useCustomSitePreference from '../../../hooks/forms/useCustomSitePreference'
import {BundleItemAccordion} from '../bundle-info'
import {highlightIngredientsWithAllergens, allergensProductMayContain, formatAllergens} from '../../../utils/allergen-ingredients-utils'

interface ProductDetailAccordionProps extends BoxProps<'div'> {
  defaultOpen?: boolean
  children: ReactNode
  title: string
}

const ProductDetailAccordion = React.forwardRef((props: ProductDetailAccordionProps,  ref: React.Ref<HTMLDivElement>) => {
  const {children, title, defaultOpen = false, ...rest} = props
  const content = <Box>{children}</Box>
  return (
    <CollapsablePanel
      defaultOpen={defaultOpen}
      panelContent={content}
      borderBottom="1px"
      paddingY="16px"
      paddingX="20px"
      borderColor="gray200"
      spacing={20}
      style={{marginBottom: -1}}
      {...rest}
      ref={ref}
    >
      {({onToggle, isOpen}) => {
        return (
          <Flex
            className={pdpAccordionToggle}
            borderRadius="none"
            justifyContent="space-between"
            height="auto"
            width="full"
            alignItems="center"
            onClick={onToggle}
            data-test-selector={`accordion-${toSlug(title)}`}
          >
            <Text variant="text6">{title}</Text>
            <ChevronUpIcon
              boxSize="20px"
              data-test-selector={`accordion-${toSlug(title)}-chevron`}
              style={{
                transform: `rotate(${isOpen ? 0 : 180}deg)`,
                transition: 'transform 200ms ease-out',
              }}
            />
          </Flex>
        )
      }}
    </CollapsablePanel>
  )
})

interface ProductInfoModuleProps {
  productDetail: ReturnType<typeof useProductDetail>
  productInformation: ProductInformation
  pdpAwardSlot: PDPAwardSlot | null
  selectedIndex: number // Add this prop to receive the active tab index
  onTabChange: (index: number) => void // Add this prop to handle tab changes
  reviewsTabRef: React.RefObject<HTMLDivElement> // Add this prop to receive the ref for the reviews tab
  isBundle: boolean | undefined
}


export const ProductInfoModule = ({
  productDetail,
  productInformation,
  pdpAwardSlot,
  selectedIndex,
  onTabChange,
  reviewsTabRef,
  isBundle,
}: ProductInfoModuleProps) => {
  const nutritionModal = useDisclosure()
  const [writeReviewUrl, setWriteReviewUrl] = useState(null)
  const [globalAllergenWords, setGlobalAllergenWords] = useState<string[]>([]);
  const {productId, product} = productDetail
  const {isRegistered} = useCustomerStore()
  const {getCustomSitePreferenceById, customSitePreferences, isCustomSitePreferenceSet} = useGlobalStore()
  const cms = useContentStore()
  const [pdpProductInformationBannerKey, setPdpProductInformationBannerKey] = useState<string | null>(null)
  const tabTitle = isBundle ? 'About the Bundle' : 'Quick Look'

  useEffect(() => {
    const fetchReviewUrl = async () => {
      if (isRegistered && writeReviewUrl === null) {
        const response = await fetch(
          `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Product-GetFeefoSmartlinkUrl?productID=${productId}`,
        ).then((response) => response.json())

        setWriteReviewUrl(response.feefoSmartlinkUrl)
      }
    }
    fetchReviewUrl().catch(console.error)
  }, [productId, isRegistered])

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('globalAllergenWords')) {
        await getCustomSitePreferenceById('globalAllergenWords')
      }
      if (Array.isArray(customSitePreferences['globalAllergenWords'])) {
        setGlobalAllergenWords(customSitePreferences['globalAllergenWords'] as Array<string>)
      }
    })()
  }, [])

  const {
    alcoholUnits, 
    alcoholVolume,
    shortDescription,
    dietaryInformation,
    manufacturerAddress,
    manufacturerReturnTo,
    packageType,
    storageInformation,
    cookinginstructions,
    ingredients,
    allergenContains,
    allergenInfo,
    nutritionInformation,
    allergenMayContain,
    pdpProductInformationBanner,
    directionsForUse,
    mandatoryWarning
  } = product
  // Stops PDP from breaking if allergenContains and allergenMayContain are undefined
  const allergens = allergenContains || []
  const allergensProductMayContainList = allergensProductMayContain(allergenMayContain) || []
  const showIngredients = ingredients || allergenContains || allergenMayContain || allergenInfo
  const showNutritionalTable = nutritionInformation && Array.isArray(nutritionInformation?.nutrients) && nutritionInformation.nutrients.length > 0
   const showReviews = useCustomSitePreference('ShowReviews')
   const showLeaveAReview = useCustomSitePreference('showLeaveAReview')
  // Fetch Individual Product Banner Content from Amplience
  useEffect(() => {
    if (pdpProductInformationBanner) {
      (async function () {
        if (!cms.contentById[pdpProductInformationBanner]) {
          await cms.fetchItemByKey(pdpProductInformationBanner)
        }
        setPdpProductInformationBannerKey(pdpProductInformationBanner)
      })()
    }
  }, [])

  const tabPanels = [
    <TabPanel title={tabTitle} position="relative" data-test-selector="pdp-quick-look" id="quick-look">
      {isBundle && (
        <Stack 
        spacing="0px"
        paddingX="20px"
        paddingY="16px"
        borderBottom="1px"
        borderColor="gray200"
        style={{marginBottom: -1}}
        >
          <Text variant="text3">
            {shortDescription}
          </Text>
        </Stack>
      )}
      {showNutritionalTable && (
        <Stack
          display="flex"
          paddingX="20px"
          paddingY="16px"
          borderBottom="1px"
          borderColor="gray200"
          style={{marginBottom: -1}}
        >
              <Text variant="heading0">Nutrition Info</Text>
              <Text
                variant="unstyled"
                textDecoration="underline"
                fontSize="sm"
                cursor="pointer"
                onClick={() => nutritionModal.onOpen()}
                color="accent5"
              >
                Show full nutritional table
              </Text>
              <NutritionalModal nutritionInformation={nutritionInformation} {...nutritionModal} />
        </Stack>
      )}
      {cookinginstructions && cookinginstructions.length > 0 && <CookingInstructions instructions={cookinginstructions} />}
      <Stack spacing="0px">
        {showIngredients && (
          <ProductDetailAccordion
            display="block"
            defaultOpen={false}
            title="Ingredients"
          >
            <Stack spacing="16px">
              {ingredients && (
                <Stack spacing="16px">
                  {ingredients.map((ingredientsText: string, index: number) => (
                    <Text
                      key={index}
                      variant="text3"
                      color="gray1000"
                      lineHeight="short"
                      dangerouslySetInnerHTML={{
                        __html: highlightIngredientsWithAllergens(
                          ingredientsText,
                          [...allergens, ...globalAllergenWords],
                        ),
                      }}
                    />
                  ))}
                </Stack>
              )}
              {allergenInfo && (
                <Stack>
                  <Text variant="text3" color="gray1000" lineHeight="short">
                    {allergenInfo}
                  </Text>
                </Stack>
              )}
              {allergenContains && (
                <Stack spacing="24px">
                  <Text variant="text6" fontWeight="normal" fontSize="md" lineHeight="base">
                    Allergy Information
                  </Text>
                  <Text variant="text3" color="gray1000" lineHeight="short">
                    Contains: <span style={{ fontWeight: 'bold' }}>
                      {ingredients
                        ? formatAllergens(ingredients.join(', '), allergenContains, globalAllergenWords)
                        : allergenContains.map((allergen: string) => capitalize(allergen)).join(", ")
                      }
                    </span>
                  </Text>
                </Stack>
              )}
              {allergensProductMayContainList.length != 0 && (
                <Text variant="text3" color="gray1000" lineHeight="short">
                  May contain: <span style={{ fontWeight: 'bold' }}>{allergensProductMayContainList}</span>
                </Text>
              )}
            </Stack>
          </ProductDetailAccordion>
        )}
    {!isBundle && (
      <ProductDetailAccordion
        defaultOpen={false}
        title={ product.bundle ? "About the Bundle": "Additional Info"}
      >
        <Stack spacing="24px">
          {shortDescription && (
            <Text variant="text3" lineHeight="shorter">
              {shortDescription}
            </Text>
          )}
          {dietaryInformation && (
            <Stack spacing="16px">
              <Text variant="text3">Dietary Information</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {dietaryInformation}
              </Text>
            </Stack>
          )}
          {manufacturerAddress && (
            <Stack spacing="16px">
              <Text variant="text3">Manufacturers Address</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {manufacturerAddress}
              </Text>
            </Stack>
          )}
          {manufacturerReturnTo && (
            <Stack spacing="16px">
              <Text variant="text3">Return To</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {manufacturerReturnTo}
              </Text>
            </Stack>
          )}
          {packageType && (
            <Stack spacing="16px">
              <Text variant="text3">Package type</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {packageType}
              </Text>
            </Stack>
          )}
          {alcoholVolume && (
            <Stack spacing="16px">
              <Text variant="text3">Alcohol Volume</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {alcoholVolume}%
              </Text>
            </Stack>
          )}
          {alcoholUnits && (
            <Stack spacing="16px">
              <Text variant="text3">Alcohol Units</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {alcoholUnits}
              </Text>
            </Stack>
          )}
          {storageInformation && (
            <Stack spacing="16px">
              <Text variant="text3">Storage information</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {storageInformation}
              </Text>
            </Stack>
          )}
          {directionsForUse && (
            <Stack spacing="16px">
              <Text variant="text3">Directions for Use</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {directionsForUse}
              </Text>
            </Stack>
          )}
          {mandatoryWarning && (
            <Stack spacing="16px">
              <Text variant="text3">Warning</Text>
              <Text variant="text3" color="gray1000" lineHeight="shorter">
                {mandatoryWarning}
              </Text>
            </Stack>
          )}
        </Stack>
      </ProductDetailAccordion>
    )}
    </Stack>
    {isBundle && (
      <Stack padding="20px" spacing="16px" borderBottom={"1px"} borderColor="gray200" style={{ marginBottom: -1, paddingBottom: "36px" }}>
        <Text variant="text6"> Included in the Bundle</Text>
        <BundleItemAccordion bundledItems={product.bundledProducts}></BundleItemAccordion>
      </Stack>
    )}
    <Stack padding="20px" spacing="16px">
      <Text variant="text6">{productInformation?.title}</Text>
      <ReactMarkdown
        components={{
          p: ({...props}) => {
            return (
              <Text variant="text1" lineHeight="short">
                {props?.children}
              </Text>
            )
          },
        }}
      >
        {productInformation?.copy || ''}
      </ReactMarkdown>
    </Stack>
  </TabPanel>

  ]

  if ((showReviews || showLeaveAReview) && !isBundle) {
    tabPanels.push(
      <TabPanel
          title={`Product Reviews`}
          data-test-selector="pdp-reviews"
          id="reviews"
          ref={reviewsTabRef}
        >
          <Box>
            <FeefoReviews
              productId={productId}
              writeReviewUrl={writeReviewUrl}
              hasReviews={!!product.productReviewCount}
            />
          </Box>
      </TabPanel>
    )
  }

  return (
    <Flex gap="20px">
      <Stack className={infoLeftColumn} bg="white" flexGrow="1">
        <Tabs
          id="pdpTabs"
          selectedIndex={selectedIndex} // Use the selectedIndex prop to control active tab
          onTabChange={onTabChange} // Handle tab changes
          switcherContent={
            (pdpProductInformationBannerKey || pdpAwardSlot) && (
            <Stack spacing="4px" borderBottom="1px" borderColor="gray200" padding="16px">
              {/* We only want to show one piece of content here: Individual Content > Awards Banners */}
              {pdpProductInformationBannerKey ? <Slot data={cms.contentById[pdpProductInformationBanner]} deliveryKey={pdpProductInformationBanner} /> 
              : pdpAwardSlot ? <Slot data={pdpAwardSlot as unknown as SlotContentItem} /> 
              : null}
            </Stack>
            )
          }
        >
          {tabPanels}
        </Tabs>
      </Stack>
    </Flex>
  )
}